import { FormSection } from "Components/Shared/FormSection";
import { DragNDrop } from "Components/Shared/DragNDrop";
import { Dropdown } from "Components/Shared/Inputs/Dropdown";
import {
  SECOND_DOCUMENT_TYPE_OPTIONS,
  SecondDocumentTypes,
  type SecondDocumentType,
} from "Components/Onboarding/shared/DocumentsUpload/types";
import { Resources, useResource } from "Translations/Resources";
import { Stack } from "@mui/material";

const FORM_RESOURCES = Resources.Onboarding.Steps.Documents.Forms;
const FILE_RESOURCES = Resources.Components.Account.SecondDocumentUpload;
const FIELD_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Form.Fields
    .RelatedPartyFields;
const SECOND_DOCUMENT_RESOURCES = Resources.Onboarding.Steps.Documents;

export const DOCUMENT_LEGEND_PROPS = {
  textTransform: "none",
  fontWeight: 400,
} as const;

export type StatutoryBodyDocuments = {
  firstFront: File | null;
  firstBack: File | null;
  secondType: SecondDocumentType | "";
  secondFront: File | null;
  secondBack: File | null;
};

export const EMPTY_DOCUMENTS: StatutoryBodyDocuments = {
  firstFront: null,
  firstBack: null,
  secondType: "",
  secondFront: null,
  secondBack: null,
};

type Props = {
  value: StatutoryBodyDocuments;
  onChange: (value: StatutoryBodyDocuments) => void;
};

export const StatutoryBodyDocumentsFields = ({ value, onChange }: Props) => {
  const { t } = useResource();

  const { firstFront, firstBack, secondType, secondFront, secondBack } = value;
  const isDrivingLicense = secondType === SecondDocumentTypes.DrivingLicense;

  const handleUpdate = (patch: Partial<StatutoryBodyDocuments>) =>
    onChange({ ...value, ...patch });

  return (
    <Stack gap={4}>
      <FormSection
        legend={t(FIELD_RESOURCES.Legend.FirstDocument)}
        legendProps={DOCUMENT_LEGEND_PROPS}
        noMargins
      >
        <Stack gap={2}>
          <DragNDrop
            file={firstFront}
            onChange={(file) => handleUpdate({ firstFront: file })}
            title={t(FORM_RESOURCES.FrontSide)}
            description={t(FILE_RESOURCES.FileDescription)}
          />
          <DragNDrop
            file={firstBack}
            onChange={(file) => handleUpdate({ firstBack: file })}
            title={t(FORM_RESOURCES.BackSide)}
            description={t(FILE_RESOURCES.FileDescription)}
          />
        </Stack>
      </FormSection>
      <FormSection
        legend={t(SECOND_DOCUMENT_RESOURCES.SecondDocumentTitle)}
        legendProps={DOCUMENT_LEGEND_PROPS}
        noMargins
      >
        <Stack gap={2}>
          <Dropdown
            name="secondDocumentType"
            label={t(FORM_RESOURCES.SecondDocumentTypeLabel)}
            value={secondType}
            options={SECOND_DOCUMENT_TYPE_OPTIONS.map((option) => ({
              Value: option.value,
              Name: t(option.labelKey),
            }))}
            onChange={(value) =>
              handleUpdate({
                secondType: value as SecondDocumentType | "",
                secondBack: null,
              })
            }
          />
          {secondType && (
            <DragNDrop
              file={secondFront}
              onChange={(secondFront) => handleUpdate({ secondFront })}
              title={t(FORM_RESOURCES.SecondDocumentFrontSide)}
              description={t(FILE_RESOURCES.FileDescription)}
            />
          )}
          {isDrivingLicense && (
            <DragNDrop
              file={secondBack}
              onChange={(secondBack) => handleUpdate({ secondBack })}
              title={t(FORM_RESOURCES.SecondDocumentBackSide)}
              description={t(FILE_RESOURCES.FileDescription)}
            />
          )}
        </Stack>
      </FormSection>
    </Stack>
  );
};
